import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, IconButton, Collapse, useMediaQuery, useTheme } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import SchoolIcon from '@mui/icons-material/School';
import CastForEducationIcon from '@mui/icons-material/CastForEducation';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

/**
 * Dashboard widget component that displays system statistics.
 * Shows real-time information about users, courses, and active sessions.
 * Collapsible on mobile devices.
 *
 * @returns The Dashboard Widget UI
 */
const DashboardWidget = () => {
    const [stats, setStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { user } = useAuth();

    // Default collapsed on mobile, expanded on desktop
    const [isExpanded, setIsExpanded] = useState(!isMobile);
    const [displayStats, setDisplayStats] = useState<any[]>([]);

    // Update expanded state when screen size changes
    useEffect(() => {
        setIsExpanded(!isMobile);
    }, [isMobile]);

    useEffect(() => {
        const fetchStats = async () => {
            if (!user) return;

            try {
                if (user.role === 'super-admin') {
                    const res = await api.get('/api/stats/dashboard');
                    setDisplayStats([
                        {
                            label: 'Utilisateurs',
                            value: res.data.users?.total || 0,
                            color: '#4caf50',
                            icon: <PersonIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                        },
                        {
                            label: 'Organismes',
                            value: res.data.organizations?.total || 0,
                            color: '#2196f3',
                            icon: <BusinessIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                        },
                        {
                            label: 'Mails Envoyés',
                            value: res.data.emails?.total || 0,
                            color: '#ff9800',
                            icon: <EmailIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                        }
                    ]);
                } else if (user.role === 'of-admin' && user.organizationId) {
                    const orgId = typeof user.organizationId === 'string' ? user.organizationId : user.organizationId._id;
                    const res = await api.get(`/api/organizations/${orgId}/stats`);
                    setDisplayStats([
                        {
                            label: 'Apprenants',
                            value: res.data.learnersCount || 0,
                            color: '#4caf50',
                            icon: <SchoolIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                        },
                        {
                            label: 'Formateurs',
                            value: res.data.trainersCount || 0,
                            color: '#9c27b0',
                            icon: <CastForEducationIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                        },
                        {
                            label: 'Inscriptions',
                            value: res.data.activeEnrollmentsCount || 0,
                            color: '#2196f3',
                            icon: <LocalOfferIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
                        }
                    ]);
                }
                setLoading(false);
            } catch (err) {
                console.error("Error fetching stats", err);
                setLoading(false);
            }
        };
        fetchStats();
    }, [user]);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <Box
            sx={{
                width: { xs: 200, sm: 250, md: 300 },
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: { xs: 3, md: 4 },
                p: { xs: 1.5, sm: 2, md: 3 },
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.2)',
                color: 'white',
                transition: 'all 0.3s ease',
            }}
        >
            {/* Header with toggle */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: isMobile ? 'pointer' : 'default',
                    mb: isExpanded ? { xs: 1, md: 2 } : 0
                }}
                onClick={isMobile ? toggleExpanded : undefined}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }
                    }}
                >
                    {isExpanded ? 'État du Système' : '📊 Stats'}
                </Typography>
                {isMobile && (
                    <IconButton
                        size="small"
                        sx={{ color: 'white', p: 0.5 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded();
                        }}
                    >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                )}
            </Box>

            {/* Collapsible content */}
            <Collapse in={isExpanded} timeout="auto">
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} sx={{ color: 'white' }} />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
                        {displayStats.map((stat, index) => (
                            <Box key={index}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {stat.icon}
                                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                                            {stat.label}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.3rem' } }}>
                                        {stat.value}
                                    </Typography>
                                </Box>
                                <Box sx={{ width: '100%', height: 4, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                                    <Box sx={{ width: '100%', height: '100%', bgcolor: stat.color, borderRadius: 2, opacity: 0.8 }} />
                                </Box>
                            </Box>
                        ))}

                        {displayStats.length === 0 && (
                            <Typography variant="caption" sx={{ textAlign: 'center', opacity: 0.6 }}>
                                Aucune donnée disponible
                            </Typography>
                        )}
                    </Box>
                )}
            </Collapse>
        </Box>
    );
};

export default DashboardWidget;
